import React, { useState, useMemo, memo } from 'react';
import { Tooltip, IconButton, Box, Typography, CircularProgress } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Modal from '../Modal';
import Button from '../Button';
import useStyles from './style';
import classNames from 'classnames';

/**
 * Format date string to readable format
 * @param {string} dateStr - ISO date string
 * @returns {string} - Formatted date or '-' if null
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date with time for tooltip display
 * @param {string} dateStr - ISO date string
 * @returns {string} - Formatted date with time
 */
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Get affected dates display string
 * @param {string} startDate - Start date ISO string
 * @param {string} endDate - End date ISO string
 * @returns {string} - Formatted date range
 */
const getAffectedDates = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = endDate ? formatDate(endDate) : 'Present';
  return `${start} - ${end}`;
};

const normalizeResyncData = (resync) => {
  if (!resync) return [];

  const values = Array.isArray(resync) ? resync : Object.values(resync);

  return values.reduce((acc, item) => {
    if (Array.isArray(item)) {
      acc.push(...normalizeResyncData(item));
    } else if (item) {
      acc.push(item);
    }

    return acc;
  }, []);
};

const SOURCE_TYPE_LABELS = {
  seedlive_trackings: 'seedlive',
  nayax_trackings: 'nayax',
};

const getSourceTypeLabel = (sourceType) => SOURCE_TYPE_LABELS[sourceType] || sourceType || '-';

/**
 * ResyncDetails Component
 * Displays resync status with icon, tooltip on hover, and detailed modal on click
 *
 * @param {Array} resyncData - Array of resync records
 */
const ResyncDetails = ({ resyncData = [], onRefresh }) => {
  const classes = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const normalizedResyncData = useMemo(() => normalizeResyncData(resyncData), [resyncData]);

  // Determine if any resync is in progress
  const hasInProgress = useMemo(
    () => normalizedResyncData.some((item) => item.status !== 'completed'),
    [normalizedResyncData],
  );

  // Get the latest resync record
  const latestResync = useMemo(() => {
    if (normalizedResyncData.length === 0) return null;

    const sorted = [...normalizedResyncData].sort((a, b) => {
      const dateA = new Date(a.job_completed_at || a.job_begins_at || a.created_at);
      const dateB = new Date(b.job_completed_at || b.job_begins_at || b.created_at);
      return dateB - dateA;
    });

    return sorted[0];
  }, [normalizedResyncData]);

  // Generate tooltip content based on latest resync status
  const tooltipContent = useMemo(() => {
    if (!latestResync) return 'No resync data available';

    if (latestResync.status !== 'completed') {
      return (
        <Box className={classes.tooltipContent}>
          <Typography variant="body2" fontWeight={600}>
            Resync In Progress
          </Typography>
          <Typography variant="caption">Started: {formatDateTime(latestResync.job_begins_at)}</Typography>
        </Box>
      );
    }

    return (
      <Box className={classes.tooltipContent}>
        <Typography variant="body2" fontWeight={600}>
          Latest Resync Completed
        </Typography>
        <Typography variant="caption">{formatDateTime(latestResync.job_completed_at)}</Typography>
      </Box>
    );
  }, [latestResync, classes.tooltipContent]);

  const handleOpenModal = async () => {
    setIsModalOpen(true);
    if (onRefresh) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Sort resync data by most recent first for modal display
  const sortedResyncData = useMemo(
    () =>
      [...normalizedResyncData].sort((a, b) => {
        const dateA = new Date(a.job_completed_at || a.job_begins_at || a.created_at);
        const dateB = new Date(b.job_completed_at || b.job_begins_at || b.created_at);
        return dateB - dateA;
      }),
    [normalizedResyncData],
  );

  // Render status chip
  const renderStatus = (status) => {
    const isCompleted = status === 'completed';
    return (
      <span
        className={classNames(classes.statusChip, {
          [classes.completedStatus]: isCompleted,
          [classes.inProgressStatus]: !isCompleted,
        })}
      >
        {isCompleted ? (
          <CheckCircleIcon sx={{ fontSize: 14 }} />
        ) : (
          <SyncIcon sx={{ fontSize: 14 }} className={classes.inProgressIcon} />
        )}
        {isCompleted ? 'Completed' : 'In Progress'}
      </span>
    );
  };

  // Check if resync feature is enabled
  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';
  if (!isResyncEnabled || normalizedResyncData.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title={tooltipContent} arrow placement="top">
        <IconButton
          className={classes.iconWrapper}
          onClick={handleOpenModal}
          data-testid="resync-details-icon"
          size="small"
        >
          {hasInProgress ? (
            <SyncIcon className={classes.inProgressIcon} />
          ) : (
            <CheckCircleIcon className={classes.completedIcon} />
          )}
        </IconButton>
      </Tooltip>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Resync Details"
        closeIcon
        maxWidth="md"
        actionButtons={
          <Button variant="contained" onClick={handleCloseModal}>
            Close
          </Button>
        }
      >
        <Box className={classes.modalContent}>
          {isRefreshing ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <table className={classes.resyncTable}>
              <thead>
                <tr>
                  <th>Sublocation Name</th>
                  <th>Asset ID</th>
                  <th>Modem ID</th>
                  <th>Source Type</th>
                  <th>Affected Dates</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedResyncData.length > 0 ? (
                  sortedResyncData.map((item) => (
                    <tr key={item.id}>
                      <td>{item['sublocation.name'] || item.sublocation?.name || '-'}</td>
                      <td>{item.asset_id || '-'}</td>
                      <td>{item.modem_id || '-'}</td>
                      <td>{getSourceTypeLabel(item.source_type)}</td>
                      <td>{getAffectedDates(item.start_date, item.end_date)}</td>
                      <td>{renderStatus(item.status)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className={classes.noDataRow}>
                      No resync records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default memo(ResyncDetails);
